<script setup lang="ts">
import type { NoteConfig, Layer } from "@/types/legend.types";
import type { PhotoMarkerShape, PhotoMarkerAttribute } from "@/drawing";
import { computed } from "vue";
import { useTranslation } from "i18next-vue";
import { format as formatDate } from "date-fns";
import { PHOTO_MARKER_DATE_FORMAT_PRESETS } from "@/defaultLegend";
import NoteConfigItem from "./NoteConfigItem.vue";

const { t } = useTranslation();

const props = defineProps<{
  modelValue: PhotoMarkerShape[];
  photoNote?: NoteConfig;
  layers: Layer[];
  photoMarkerAttribute?: PhotoMarkerAttribute;
  photoMarkerDateFormat?: string;
}>();

const emits = defineEmits<{
  (e: "update:modelValue", markers: PhotoMarkerShape[]): void;
  (e: "update:photoNote", note: NoteConfig): void;
  (e: "update:photoMarkerAttribute", value: PhotoMarkerAttribute): void;
  (e: "update:photoMarkerDateFormat", value: string): void;
  (e: "new-layer", value: { id: string; name: string }): void;
}>();

const selectedMarkers = computed({
  get: () => props.modelValue,
  set: (value) => emits("update:modelValue", value),
});

const photoNoteModel = computed({
  get: () => props.photoNote,
  set: (value) => {
    if (value) emits("update:photoNote", value);
  },
});

const attributeMode = computed<PhotoMarkerAttribute>({
  get: () => props.photoMarkerAttribute ?? "number",
  set: (value) => emits("update:photoMarkerAttribute", value),
});

const dateFormat = computed<string>({
  get: () => props.photoMarkerDateFormat ?? PHOTO_MARKER_DATE_FORMAT_PRESETS[0],
  set: (value) => emits("update:photoMarkerDateFormat", value),
});

const dateFormatPresets = PHOTO_MARKER_DATE_FORMAT_PRESETS;

const dateFormatPreview = computed(() => formatDate(new Date(), dateFormat.value));

interface MarkerOption {
  value: PhotoMarkerShape;
  label: string;
}

const markerOptions: MarkerOption[] = [
  { value: "circle", label: "dashboard:legend.photo_marker.circle" },
  { value: "thin_arrow", label: "dashboard:legend.photo_marker.thin_arrow" },
  { value: "large_arrow", label: "dashboard:legend.photo_marker.large_arrow" },
  { value: "text_only", label: "dashboard:legend.photo_marker.text_only" },
];

const isSelected = (marker: PhotoMarkerShape) => {
  return selectedMarkers.value.includes(marker);
};

const toggleMarker = (marker: PhotoMarkerShape) => {
  if (isSelected(marker)) {
    if (selectedMarkers.value.length <= 1) return;
    selectedMarkers.value = selectedMarkers.value.filter((m) => m !== marker);
  } else {
    selectedMarkers.value = [...selectedMarkers.value, marker];
  }
};

const isDisabled = (marker: PhotoMarkerShape) => {
  return isSelected(marker) && selectedMarkers.value.length <= 1;
};
</script>

<template>
  <div class="photo-markers-config">
    <!-- Photo Note Configuration -->
    <div class="mb-5" v-if="photoNoteModel">
      <h5 class="mb-3">
        <el-icon class="me-2"><i class="bi-gear"></i></el-icon>
        {{ t("dashboard:legend.photo_marker.properties") }}
      </h5>
      <div
        class="rounded p-3 border-gray overflow-hidden"
        style="background-color: var(--bs-body-bg)"
      >
        <div
          class="d-flex justify-content-between align-items-center gap-2 pb-1"
        >
          <div class="col text-dark fw-light">
            <span>{{ t("dashboard:photo.legend.description") }}</span>
          </div>
        </div>
        <NoteConfigItem
          v-model="photoNoteModel"
          :categories="[]"
          :layers="props.layers"
          type="photo"
          @new-layer="emits('new-layer', $event)"
        />
      </div>
    </div>

    <!-- Marker Shape Selection -->
    <h5 class="mb-3">
      <el-icon class="me-2"><i class="bi-diagram-3"></i></el-icon>
      {{ t("dashboard:legend.photo_marker.shapes") }}
    </h5>
    <p class="text-muted mb-4">
      {{ t("dashboard:legend.photo_marker.description") }}
    </p>

    <div class="d-flex flex-wrap gap-4">
      <div
        v-for="option in markerOptions"
        :key="option.value"
        class="marker-option"
        :class="{
          selected: isSelected(option.value),
          disabled: isDisabled(option.value),
        }"
        @click="toggleMarker(option.value)"
      >
        <div class="marker-preview">
          <!-- Circle marker preview -->
          <svg
            v-if="option.value === 'circle'"
            viewBox="-3 -3 6 6"
            class="marker-svg"
          >
            <circle
              cx="0"
              cy="0"
              r="2"
              fill="none"
              stroke="currentColor"
              stroke-width="0.3"
            />
            <text
              x="0"
              y="0"
              text-anchor="middle"
              dominant-baseline="central"
              font-size="1.5"
              fill="currentColor"
            >
              1
            </text>
          </svg>

          <!-- Thin arrow marker preview -->
          <svg
            v-else-if="option.value === 'thin_arrow'"
            viewBox="-1.5 -2 3 5"
            class="marker-svg"
          >
            <polyline
              points="0,2 0,0.5"
              fill="none"
              stroke="currentColor"
              stroke-width="0.2"
            />
            <polyline
              points="-0.5,1.5 0,2 0.5,1.5"
              fill="none"
              stroke="currentColor"
              stroke-width="0.2"
            />
            <text
              x="0"
              y="-0.8"
              text-anchor="middle"
              dominant-baseline="central"
              font-size="1.2"
              fill="currentColor"
            >
              1
            </text>
          </svg>

          <!-- Large arrow marker preview -->
          <svg
            v-else-if="option.value === 'large_arrow'"
            viewBox="-1.5 -2 3 5"
            class="marker-svg"
          >
            <polygon
              points="0,2 0.5,1.5 0.25,1.5 0.25,0.5 -0.25,0.5 -0.25,1.5 -0.5,1.5"
              fill="currentColor"
              stroke="currentColor"
              stroke-width="0.1"
            />
            <text
              x="0"
              y="-0.8"
              text-anchor="middle"
              dominant-baseline="central"
              font-size="1.2"
              fill="currentColor"
            >
              1
            </text>
          </svg>

          <!-- Text only marker preview -->
          <svg
            v-else-if="option.value === 'text_only'"
            viewBox="-3 -3 6 6"
            class="marker-svg"
          >
            <text
              x="0"
              y="0"
              text-anchor="middle"
              dominant-baseline="central"
              font-size="3"
              fill="currentColor"
            >
              1
            </text>
          </svg>
        </div>

        <div class="marker-label">
          {{ t(option.label) }}
        </div>

        <div class="marker-checkbox">
          <el-checkbox
            :model-value="isSelected(option.value)"
            :disabled="isDisabled(option.value)"
            @click.stop
            @change="toggleMarker(option.value)"
          />
        </div>
      </div>
    </div>

    <!-- Marker Attribute Content -->
    <h5 class="mb-3 mt-5">
      <el-icon class="me-2"><i class="bi-clock"></i></el-icon>
      {{ t("dashboard:legend.photo_marker.attribute.title") }}
    </h5>
    <p class="text-muted mb-3">
      {{ t("dashboard:legend.photo_marker.attribute.description") }}
    </p>

    <div class="d-flex gap-2 mb-3">
      <button
        type="button"
        class="attribute-mode-btn"
        :class="{ active: attributeMode === 'number' }"
        @click="attributeMode = 'number'"
      >
        {{ t("dashboard:legend.photo_marker.attribute.number") }}
      </button>
      <button
        type="button"
        class="attribute-mode-btn"
        :class="{ active: attributeMode === 'date' }"
        @click="attributeMode = 'date'"
      >
        {{ t("dashboard:legend.photo_marker.attribute.date") }}
      </button>
    </div>

    <div v-if="attributeMode === 'date'" class="d-flex align-items-center gap-3">
      <el-select v-model="dateFormat" style="width: 220px">
        <el-option
          v-for="preset in dateFormatPresets"
          :key="preset"
          :value="preset"
          :label="preset"
        />
      </el-select>
      <span class="text-muted">
        {{
          t("dashboard:legend.photo_marker.attribute.preview", {
            value: dateFormatPreview,
          })
        }}
      </span>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.photo-markers-config {
  padding: 1rem 0;
}

.marker-option {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1rem;
  border: 2px solid var(--bs-border-color);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 120px;

  &:hover:not(.disabled) {
    border-color: var(--bs-primary);
    background-color: var(--bs-light);
  }

  &.selected {
    border-color: var(--bs-primary);
    background-color: rgba(var(--bs-primary-rgb), 0.1);
  }

  &.disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
}

.attribute-mode-btn {
  padding: 0.5rem 1.5rem;
  border: 2px solid var(--bs-border-color);
  border-radius: 8px;
  background: transparent;
  color: var(--bs-body-color);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--bs-primary);
    background-color: var(--bs-light);
  }

  &.active {
    border-color: var(--bs-primary);
    background-color: rgba(var(--bs-primary-rgb), 0.1);
  }
}

.marker-preview {
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.5rem;
}

.marker-svg {
  width: 100%;
  height: 100%;
  color: var(--bs-body-color);
}

.marker-label {
  font-size: 0.9rem;
  text-align: center;
  margin-bottom: 0.5rem;
}

.marker-checkbox {
  margin-top: auto;
}
</style>
